import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { projectsApi } from '../../services/projectsApi.js';
import { todayIso } from '../../utils/formatters.js';

const CATEGORIES = ['TRAVEL', 'MEALS', 'ACCOMMODATION', 'TRANSPORT', 'SUPPLIES', 'OTHER'];

function emptyForm() {
  return {
    projectId: '',
    employeeId: '',
    expenseDate: todayIso(),
    category: 'TRAVEL',
    amount: '',
    description: '',
    billable: false,
    receiptUrl: '',
  };
}

export function ExpenseModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState(null);

  const projects = useQuery({
    queryKey: ['projects', 'list', 'for-expense'],
    queryFn: () => projectsApi.listProjects({ limit: 200 }).then((r) => r.items),
    enabled: open,
  });
  const employees = useQuery({
    queryKey: ['hr', 'employees', 'for-expense'],
    queryFn: () => hrApi.listEmployees({ limit: 200 }).then((r) => r.items),
    enabled: open,
  });

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const create = useMutation({
    mutationFn: (body) => projectsApi.createExpense(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'expenses'] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'expenses-by-category'] });
      setForm(emptyForm());
      setError(null);
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    const employee = (employees.data || []).find((emp) => emp.id === form.employeeId);
    create.mutate({
      projectId: form.projectId || null,
      employeeId: form.employeeId || null,
      employeeName: employee ? `${employee.firstName} ${employee.lastName}` : null,
      expenseDate: form.expenseDate,
      category: form.category,
      amount: form.amount || 0,
      description: form.description || null,
      billable: form.billable,
      receiptUrl: form.receiptUrl || null,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('expense.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? `${t('common.save')}…` : t('common.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('expense.field.date')}</span>
            <input className="input" type="date" value={form.expenseDate} onChange={set('expenseDate')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('expense.field.category')}</span>
            <select className="select" value={form.category} onChange={set('category')}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{t(`expense.category.${c}`)}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('expense.field.amount')}</span>
            <input className="input" type="number" min="0" step="0.05" value={form.amount} onChange={set('amount')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('expense.field.employee')}</span>
            <select className="select" value={form.employeeId} onChange={set('employeeId')}>
              <option value="">{t('common.none')}</option>
              {(employees.data || []).map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>
              ))}
            </select>
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('expense.field.project')}</span>
          <select className="select" value={form.projectId} onChange={set('projectId')}>
            <option value="">{t('common.none')}</option>
            {(projects.data || []).map((p) => (
              <option key={p.id} value={p.id}>{p.code} - {p.name}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label">{t('expense.field.description')}</span>
          <input className="input" value={form.description} onChange={set('description')} />
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('expense.field.receipt')}</span>
            <input className="input" type="url" pattern="^https?://.*" value={form.receiptUrl} onChange={set('receiptUrl')} placeholder="https://…" />
          </label>
          <label className="field" style={{ justifyContent: 'flex-end' }}>
            <span className="time-billable">
              <input type="checkbox" checked={form.billable} onChange={set('billable')} />
              {t('expense.field.billable')}
            </span>
          </label>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
